export interface OrderItemsTable {
  Row: {
    id: string;
    order_id: string | null;
    product_id: string | null;
    quantity: number;
    price_at_time: number;
    created_at: string;
  };
  Insert: {
    id?: string;
    order_id?: string | null;
    product_id?: string | null;
    quantity: number;
    price_at_time: number;
    created_at?: string;
  };
  Update: Partial<OrderItemsTable['Insert']>;
}

export interface OrdersTable {
  Row: {
    id: string;
    customer_id: string | null;
    florist_id: string | null;
    status: OrderStatus;
    total_amount: number;
    commission_amount: number;
    delivery_address: string | null;
    delivery_time: string | null;
    is_delivery: boolean | null;
    stripe_payment_id: string | null;
    created_at: string;
    updated_at: string;
    order_number: string | null;
    customer_email: string | null;
    customer_name: string | null;
    customer_phone: string | null;
    payment_status: string | null;
    stripe_session_id: string | null;
    stripe_payment_intent_id: string | null;
    refund_id: string | null;
    is_same_day: boolean | null;
    special_instructions: string | null;
    items: any[] | null;
    delivery_type: string | null;
    delivery_date: string | null;
  };
  Insert: {
    id?: string;
    customer_id?: string | null;
    florist_id?: string | null;
    status?: OrderStatus;
    total_amount: number;
    commission_amount: number;
    delivery_address?: string | null;
    delivery_time?: string | null;
    is_delivery?: boolean | null;
    stripe_payment_id?: string | null;
    created_at?: string;
    updated_at?: string;
    order_number?: string | null;
    customer_email?: string | null;
    customer_name?: string | null;
    customer_phone?: string | null;
    payment_status?: string | null;
    stripe_session_id?: string | null;
    stripe_payment_intent_id?: string | null;
    refund_id?: string | null;
    is_same_day?: boolean | null;
    special_instructions?: string | null;
    items?: any[] | null;
    delivery_type?: string | null;
    delivery_date?: string | null;
  };
  Update: Partial<OrdersTable['Insert']>;
}

export type OrderStatus = 
  | 'pending'
  | 'confirmed'
  | 'processing'
  | 'preparing'
  | 'ready_for_pickup'
  | 'picked_up'
  | 'out_for_delivery'
  | 'delivered'
  | 'cancelled'
  | 'failed_delivery'
  | 'refunded'
  | 'succeeded';

interface OrderStatusConfig {
  status: OrderStatus;
  label: string;
  description: string;
  color: string;
}

export const ORDER_STATUSES: OrderStatusConfig[] = [
  {
    status: 'pending',
    label: 'Pending Confirmation',
    description: 'A new order has been received and is awaiting your confirmation.',
    color: 'bg-gray-500'
  },
  {
    status: 'confirmed',
    label: 'Order Confirmed',
    description: 'You have confirmed this order. Next step: prepare the items.',
    color: 'bg-blue-500'
  },
  {
    status: 'preparing',
    label: 'Preparing Order',
    description: 'The order is currently being prepared for delivery/collection.',
    color: 'bg-yellow-500'
  },
  {
    status: 'ready_for_pickup',
    label: 'Ready for Pickup',
    description: 'The order is ready for customer pickup.',
    color: 'bg-purple-500'
  },
  {
    status: 'picked_up',
    label: 'Picked Up',
    description: 'The order has been picked up by the customer.',
    color: 'bg-green-500'
  },
  {
    status: 'out_for_delivery',
    label: 'Out for Delivery',
    description: 'The order is now out for delivery to the customer.',
    color: 'bg-purple-500'
  },
  {
    status: 'delayed',
    label: 'Delivery Delayed',
    description: 'The delivery of this order is currently delayed. Consider contacting the customer.',
    color: 'bg-orange-500'
  },
  {
    status: 'delivered',
    label: 'Order Delivered',
    description: 'The order has been successfully delivered to the customer.',
    color: 'bg-green-500'
  },
  {
    status: 'cancelled',
    label: 'Order Cancelled',
    description: 'This order has been cancelled.',
    color: 'bg-red-500'
  }
];

export interface Customer {
  id: string;
  email: string;
  full_name: string;
}

export interface OrderItem {
  id: string;
  order_id: string;
  product_id: string;
  quantity: number;
  unit_price: number;
  total_price: number;
  customization_notes?: string;
}

export interface Order {
  id: string;
  florist_id: string;
  customer_id: string;
  customer?: Customer;
  status: 'pending' | 'confirmed' | 'preparing' | 'ready' | 'out_for_delivery' | 'delivered' | 'cancelled';
  total_amount: number;
  delivery_fee?: number;
  delivery_address: string;
  delivery_instructions?: string;
  delivery_date: string;
  delivery_time: string;
  created_at: string;
  updated_at: string;
  items?: OrderItem[];
  payment_status: 'pending' | 'paid' | 'failed' | 'refunded';
  payment_method?: string;
  transaction_id?: string;
  order_number?: string;
  customer_email?: string;
  customer_name?: string;
  customer_phone?: string;
  stripe_session_id?: string;
  stripe_payment_intent_id?: string;
  is_same_day?: boolean;
  special_instructions?: string;
  delivery_type?: string;
}
